import { useState } from "react";
import { useModelConfigs, useUpdateModelConfig } from "../hooks/useIngestion";
import { ModelConfig } from "../lib/ingestion";
import LoadingSpinner from "../components/LoadingSpinner";

interface ConfigRowProps {
  config: ModelConfig;
  onUpdate: (modelKey: string, update: Partial<ModelConfig>) => void;
  updating: boolean;
}

function ConfigRow({ config, onUpdate, updating }: ConfigRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [threshold, setThreshold] = useState(config.threshold_similarity);
  const [enabled, setEnabled] = useState(config.enabled);
  const [selectedFields, setSelectedFields] = useState<Set<string>>(
    new Set(config.hashing_fields),
  );

  const handleSave = () => {
    onUpdate(config.model_key, {
      enabled,
      threshold_similarity: threshold,
      hashing_fields: Array.from(selectedFields),
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setThreshold(config.threshold_similarity);
    setEnabled(config.enabled);
    setSelectedFields(new Set(config.hashing_fields));
    setIsEditing(false);
  };

  const toggleField = (field: string) => {
    const newFields = new Set(selectedFields);
    if (newFields.has(field)) {
      newFields.delete(field);
    } else {
      newFields.add(field);
    }
    setSelectedFields(newFields);
  };

  return (
    <tr className="" data-testid={`config-row-${config.model_key}`}>
      <td className="px-4 py-3 text-sm font-medium text-gray-900">
        {config.display_name}
      </td>
      <td className="px-4 py-3">
        {isEditing ? (
          <label className="inline-flex items-center cursor-pointer gap-2">
            <input
              type="checkbox"
              data-testid={`config-enabled-${config.model_key}`}
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="form-checkbox h-4 w-4 text-primary-600 rounded"
              aria-required="true"
              aria-label={`Enable deduplication for ${config.display_name}`}
            />
            <span className="text-sm text-gray-700">
              {enabled ? "Enabled" : "Disabled"}
            </span>
          </label>
        ) : (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
              config.enabled
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-700"
            }`}
          >
            {config.enabled ? "Enabled" : "Disabled"}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-gray-700">
        {isEditing ? (
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="50"
              max="99"
              value={threshold * 100}
              onChange={(e) => setThreshold(parseInt(e.target.value) / 100)}
              className="flex-1 accent-primary-600"
              aria-required="true"
              aria-label={`Similarity threshold for ${config.display_name}`}
              aria-valuemin={50}
              aria-valuemax={99}
              aria-valuenow={Math.round(threshold * 100)}
              aria-describedby="update-error"
            />
            <span className="text-sm font-medium text-gray-900 w-12">
              {(threshold * 100).toFixed(0)}%
            </span>
          </div>
        ) : (
          <span className="text-sm text-gray-700">
            {(config.threshold_similarity * 100).toFixed(0)}%
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        {isEditing ? (
          <div className="space-y-1">
            {/* Show all available fields from normalization_rules */}
            {Object.keys(config.normalization_rules).map((field) => (
              <label
                key={field}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedFields.has(field)}
                  onChange={() => toggleField(field)}
                  className="form-checkbox h-4 w-4 text-primary-600 rounded"
                />
                <span className="text-sm text-gray-700">{field}</span>
              </label>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-1">
            {config.hashing_fields.map((field) => (
              <span
                key={field}
                className="inline-flex items-center rounded px-2 py-1 bg-primary-100 text-primary-800 text-xs font-medium"
              >
                {field}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {config.readonly_fields.map((field) => (
            <span
              key={field}
              className="inline-flex items-center rounded px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium"
            >
              {field}
            </span>
          ))}
        </div>
      </td>
      <td className="px-4 py-3">
        {isEditing ? (
          <div className="flex gap-2">
            <button
              data-testid={`config-save-${config.model_key}`}
              onClick={handleSave}
              disabled={updating}
              className="inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 text-xs bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800"
              aria-describedby="update-error"
            >
              Save
            </button>
            <button
              data-testid={`config-cancel-${config.model_key}`}
              onClick={handleCancel}
              disabled={updating}
              className="inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 text-xs bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 active:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            data-testid={`config-edit-${config.model_key}`}
            onClick={() => setIsEditing(true)}
            className="inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 text-xs bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 active:bg-gray-100"
          >
            Edit
          </button>
        )}
      </td>
    </tr>
  );
}

export default function AdminModelConfig() {
  const { data: configs, isLoading, error } = useModelConfigs();
  const updateMutation = useUpdateModelConfig();

  const handleUpdate = (modelKey: string, update: Partial<ModelConfig>) => {
    updateMutation.mutate({
      modelKey,
      update: {
        enabled: update.enabled,
        threshold_similarity: update.threshold_similarity,
        hashing_fields: update.hashing_fields,
        normalization_rules: update.normalization_rules,
      },
    });
  };

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center min-h-screen bg-gray-50"
        role="status"
        aria-live="polite"
        data-testid="model-config-loading"
      >
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex items-center justify-center min-h-screen bg-gray-50"
        data-testid="model-config-error"
      >
        <div className="text-sm text-gray-500">
          Error loading configurations:{" "}
          {error instanceof Error ? error.message : "Unknown error"}
        </div>
      </div>
    );
  }

  const configArray = configs ? Object.values(configs) : [];

  return (
    <div
      className="container mx-auto px-4 py-8"
      data-testid="model-config-view"
    >
      <div className="mb-6">
        <h1
          className="text-3xl font-bold text-gray-900"
          data-testid="model-config-heading"
        >
          Ingestion Model Configuration
        </h1>
        <p className="text-gray-600 mt-2">
          Configure duplicate detection settings for each model type. Changes
          are applied in-memory.
        </p>
      </div>

      <div
        className="overflow-x-auto rounded-xl border border-gray-200"
        data-testid="model-config-table-container"
      >
        <table
          className="min-w-full divide-y divide-gray-200"
          data-testid="model-config-table"
        >
          <thead className="table-header">
            <tr>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
              >
                Model
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
              >
                Status
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
              >
                Similarity Threshold
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
              >
                Hashing Fields
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
              >
                Readonly Fields
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="data-rows">
            {configArray.map((config) => (
              <ConfigRow
                key={config.model_key}
                config={config}
                onUpdate={handleUpdate}
                updating={updateMutation.isPending}
              />
            ))}
          </tbody>
        </table>
      </div>

      {updateMutation.isError && (
        <div
          className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          role="alert"
        >
          <p id="update-error">
            Error updating configuration:{" "}
            {updateMutation.error instanceof Error
              ? updateMutation.error.message
              : "Unknown error"}
          </p>
        </div>
      )}

      <div className="mt-6 rounded-lg border border-primary-200 bg-primary-50 px-4 py-4 text-sm text-primary-800">
        <h2 className="text-sm font-semibold text-primary-900 mb-2">
          Configuration Notes
        </h2>
        <ul className="text-sm text-primary-800 space-y-1 list-disc list-inside">
          <li>
            <strong>Similarity Threshold:</strong> Records with similarity
            scores above this threshold are flagged as conflicts (50-99%)
          </li>
          <li>
            <strong>Hashing Fields:</strong> Fields used to compute content hash
            for exact duplicate detection
          </li>
          <li>
            <strong>Readonly Fields:</strong> Fields that cannot be edited
            during conflict resolution
          </li>
          <li>
            <strong>In-Memory Updates:</strong> Changes persist only for the
            current server session. For permanent changes, edit{" "}
            <code>app/config/ingestion_config.py</code>
          </li>
        </ul>
      </div>
    </div>
  );
}
